import React from 'react';
import { Search, Eye, PackageSearch, Clock, Store } from 'lucide-react';
import { Book } from '../../../types/book.types';
import { formatCurrency } from '../../../utils/currency';
import { SearchResultsSkeleton } from './SearchResultsSkeleton';
import { useOtherWarehousesStock } from '../../../hooks/useOtherWarehousesStock';
interface SearchResultsProps {
  searchQuery: string;
  filteredBooksForSearch: Book[];
  isLoading: boolean;
  error: string | null;
  detailedBookId: string | null;
  setDetailedBookId: (v: string | null) => void;
  selectedSearchSource: any | null;
  setSelectedSearchSource: (v: any | null) => void;
  addToCart: (book: Book, source?: any) => void;
  setSearchQuery: (v: string) => void;
  setNumpadValue: (v: string) => void;
  setIsSearchFocused: (v: boolean) => void;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  searchQuery,
  filteredBooksForSearch,
  isLoading,
  error,
  detailedBookId,
  setDetailedBookId,
  selectedSearchSource,
  setSelectedSearchSource,
  addToCart,
  setSearchQuery,
  setNumpadValue,
  setIsSearchFocused,
}) => {
  return (
    <div className="results-content custom-scrollbar">
      {searchQuery.length >= 2 ? (
        isLoading ? (
          <SearchResultsSkeleton />
        ) : error ? (
          <div className="results-empty">
            <Search className="results-empty-icon" />
            <p className="results-empty-text">{error}</p>
          </div>
        ) : filteredBooksForSearch.length > 0 ? (
          filteredBooksForSearch.map((book) => (
            <button
              key={book.id}
              onClick={() => {
                if (book.stock > 0) {
                  addToCart(book);
                  setSearchQuery('');
                  setNumpadValue('');
                  setIsSearchFocused(false);
                } else {
                  const isOpening = detailedBookId !== book.id;
                  setDetailedBookId(isOpening ? book.id : null);
                  if (isOpening) {
                    setSelectedSearchSource(null);
                  }
                }
              }}
              className={`results-book-item ${book.stock <= 0 ? 'results-book-item-no-stock' : 'results-book-item-stock'
                }`}
            >
              {book.stock <= 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const isOpening = detailedBookId !== book.id;
                    setDetailedBookId(isOpening ? book.id : null);
                    if (isOpening) {
                      setSelectedSearchSource(null);
                    }
                  }}
                  className="results-book-eye-btn"
                >
                  <Eye
                    className={`w-4 h-4 ${detailedBookId === book.id ? 'eye-icon-active' : ''
                      }`}
                  />
                </button>
              )}

              <span className="results-book-title">{book.title}</span>
              <span className="results-book-author">{book.author}</span>
              <div className="results-book-info-row">
                <div className="results-book-info-left">
                  <span className="results-book-isbn">ISBN: {book.isbn}</span>
                  {book.stock <= 0 && (
                    <div className="results-book-no-stock-badge">
                      <PackageSearch className="w-3 h-3" />
                      <span>Sin Stock</span>
                    </div>
                  )}
                </div>
                <div className="results-book-info-right">
                  <span className="results-book-price">
                    {formatCurrency(book.price)}
                  </span>
                </div>
              </div>

              {detailedBookId === book.id && book.sources && (
                <div className="sources-container">
                  {/*
                    Primero lo propio: si lo tiene otro almacen o libreria del
                    grupo, el cliente lo tiene en un dia y no en tres semanas.
                  */}
                  <OtherWarehouses book={book} />
                  <p className="sources-title">Pedir a un proveedor</p>
                  {book.sources.length === 0 && (
                    <p className="sources-title">No hay proveedores activos.</p>
                  )}
                  <div className="sources-list">
                    {book.sources.map((source: any, i: number) => (
                      <button
                        key={i}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSearchSource(source);
                        }}
                        className={`source-item group-source ${selectedSearchSource?.name === source.name
                          ? 'source-item-active'
                          : 'source-item-inactive'
                          }`}
                      >
                        <div className="source-item-left">
                          <span
                            className={`source-item-name ${selectedSearchSource?.name === source.name
                              ? 'source-item-name-active'
                              : 'source-item-name-inactive'
                              }`}
                          >
                            {source.name}
                          </span>
                          {source.waitTime && (
                            <span
                              className={`source-item-wait ${selectedSearchSource?.name === source.name
                                ? 'source-item-wait-active'
                                : 'source-item-wait-inactive'
                                }`}
                            >
                              <Clock className="w-2.5 h-2.5" /> {source.waitTime}
                            </span>
                          )}
                        </div>
                        <div className="source-item-right">
                          {source.price && (
                            <span
                              className={`source-item-price ${selectedSearchSource?.name === source.name
                                ? 'source-item-price-active'
                                : 'source-item-price-inactive'
                                }`}
                            >
                              {formatCurrency(source.price)}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </button>
          ))
        ) : (
          <div className="results-empty">
            <Search className="results-empty-icon" />
            <p className="results-empty-text">Sin resultados</p>
          </div>
        )
      ) : (
        <div className="results-empty">
          <div className="results-empty-icon-wrapper">
            <Search className="results-empty-icon" />
          </div>
          <p className="results-empty-text-small">Escribe título o autor</p>
        </div>
      )}
    </div>
  );
};

/**
 * Existencias en los demas almacenes/librerias de un libro sin stock aqui. Va en su propio
 * componente para que el hook solo se monte con el libro desplegado: una
 * consulta por almacen, y solo cuando el cajero la pide.
 *
 * De momento es informativo: dice donde esta para poder llamar a esa tienda. No
 * hay traspaso entre almacenes en el TPV.
 */
const OtherWarehouses: React.FC<{ book: Book }> = ({ book }) => {
  const { almacenes, isLoading } = useOtherWarehousesStock(book.isbn || book.id, true);

  if (isLoading) {
    return <p className="sources-title">Buscando en los demás almacenes…</p>;
  }
  if (almacenes.length === 0) return null;

  return (
    <div className="own-stock-block">
      <p className="sources-title">Disponible en otra librería/almacén</p>
      <div className="sources-list">
        {almacenes.map(a => (
          <div key={a.warehouseId} className="own-stock-item">
            <span className="own-stock-name">
              <Store className="w-3 h-3" /> {a.name}
            </span>
            <span className="own-stock-qty">{a.stock} ud.</span>
          </div>
        ))}
      </div>
    </div>
  );
};
